import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import logo from "@/assets/logo.webp";

type NavChild = { label: string; to: string };
type NavItem = { label: string; to: string; children?: NavChild[] };

const roomsChildren: NavChild[] = [
  { label: "Presidential Suite", to: "/rooms/presidential" },
  { label: "Executive Suites", to: "/rooms/executive" },
  { label: "Safari Suites", to: "/rooms/safari" },
  { label: "Main Lodge – Cheetah", to: "/rooms/cheetah" },
  { label: "Main Lodge – Elephant", to: "/rooms/elephant" },
  { label: "Main Lodge – Rhino", to: "/rooms/rhino" },
];

const experiencesChildren: NavChild[] = [
  { label: "Wildlife & Birding Safari", to: "/experiences#wildlife-and-birding-safari" },
  { label: "Bushveld Picnic Lunch", to: "/experiences#bushveld-picnic-lunch" },
  { label: "Guided Walks & Hiking Trails", to: "/experiences#guided-walks-and-hiking-trails" },
  { label: "Archaeological Site", to: "/experiences#archaeological-site" },
];

const culinaryChildren: NavChild[] = [
  { label: "Restaurant & Bar", to: "/culinary#restaurant-bar" },
  { label: "Boma & Private Dinner", to: "/culinary#boma-private-dinner" },
];

const spaChildren: NavChild[] = [
  { label: "FULL BODY & DEEP TISSUE MASSAGE", to: "/spa#you-have-earned-it" },
  { label: "Hot Stone Massage", to: "/spa#treat-yourself" },
  { label: "HEAD, BACK, NECK & SHOULDER MASSAGE", to: "/spa#enjoy-the-moment" },
  { label: "Reflexology & Aromatherapy", to: "/spa#tranquility" },
  { label: "African Hand & Footprint Ritual", to: "/spa#pamper" },
];

const navItems: NavItem[] = [
  { label: "HOME", to: "/" },
  { label: "ROOMS & SUITES", to: "/rooms", children: roomsChildren },
  { label: "CULINARY SAFARI", to: "/culinary", children: culinaryChildren },
  { label: "AFRICAN SPA", to: "/spa", children: spaChildren },
  { label: "SAFARI EXPERIENCES", to: "/experiences", children: experiencesChildren },
  { label: "SPECIAL OFFERS", to: "/offers" },
  
  { label: "LOCATION & ACCESS", to: "/location" },
  { label: "MEETINGS & EVENTS", to: "/events" },
  
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const [activeHash, setActiveHash] = useState<string>("");

  // Collect hash ids for the current page (children whose path matches location.pathname)
  const sectionIds = useMemo(() => {
    const ids: string[] = [];
    for (const item of navItems) {
      if (!item.children) continue;
      for (const child of item.children) {
        const [path, hash] = child.to.split("#");
        if (hash && path === location.pathname) ids.push(hash);
      }
    }
    return ids;
  }, [location.pathname]);

  useEffect(() => {
    if (sectionIds.length === 0) {
      setActiveHash("");
      return;
    }
    const compute = () => {
      const threshold = 140; // px below viewport top
      let current = "";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - threshold <= 0) current = id;
      }
      // Fallback: if none passed threshold, use first section
      if (!current) current = sectionIds[0];
      setActiveHash((prev) => (prev === current ? prev : current));
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [sectionIds, location.pathname]);

  const isChildActive = (childTo: string) => {
    const [path, hash] = childTo.split("#");
    if (hash) return path === location.pathname && activeHash === hash;
    return location.pathname === childTo;
  };


  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-lodge-dark shadow-sm">
      {/* Top bar: hamburger | logo | book now */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-12">
          <button
            className="text-[hsl(40,30%,90%)] hover:text-gold transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" className="absolute left-1/2 -translate-x-1/2">
            <img
              src={logo}
              alt="Zwahili Private Game Lodge & Spa"
              className="h-10 lg:h-9 w-auto"
            />
          </Link>

          <a
            href="https://booking.roomraccoon.co.za/zwahili-game-lodge/en/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="gold" size="sm" className="text-[10px] sm:text-xs tracking-wider h-7 sm:h-9 px-2.5 sm:px-3">
              BOOK NOW
            </Button>
          </a>
        </div>
      </div>

      {/* Desktop nav links */}
      <div className="hidden lg:block border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-10 py-1.5">
            {navItems.map((item) => {
              const isActive =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);

              const linkClass = `text-[11px] tracking-[0.2em] hover:text-gold transition-colors font-body font-medium ${
                isActive ? "text-gold" : "text-[hsl(40,30%,70%)]"
              }`;

              if (item.children) {
                return (
                  <div key={item.label} className="relative group">
                    <Link
                      to={item.to}
                      className={`${linkClass} inline-flex items-center gap-1`}
                    >
                      {item.label}
                      <ChevronDown
                        size={12}
                        className="transition-transform group-hover:rotate-180"
                      />
                    </Link>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 z-50">
                      <div className="bg-lodge-dark border border-border/50 shadow-lg min-w-[240px] py-2">
                        {item.children.map((child) => {
                          const childActive = isChildActive(child.to);
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              className={`block px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-body font-medium hover:text-gold hover:bg-lodge-dark/80 transition-colors ${
                                childActive
                                  ? "text-gold"
                                  : "text-[hsl(40,30%,70%)]"
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link key={item.label} to={item.to} className={linkClass}>
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile slide-down menu */}
      {isOpen && (
        <div className="bg-lodge-dark border-t border-border/50">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navItems.map((item) => {
              const isActive =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);

              const baseClass = `text-xs tracking-[0.2em] hover:text-gold transition-colors py-2.5 ${
                isActive ? "text-gold" : "text-[hsl(40,30%,70%)]"
              }`;

              if (item.children) {
                const expanded = mobileExpanded === item.label;
                return (
                  <div key={item.label}>
                    <div className="flex items-center justify-between">
                      <Link
                        to={item.to}
                        onClick={() => setIsOpen(false)}
                        className={`block flex-1 ${baseClass}`}
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() =>
                          setMobileExpanded(expanded ? null : item.label)
                        }
                        aria-label={`Toggle ${item.label} submenu`}
                        className="text-[hsl(40,30%,70%)] hover:text-gold p-2"
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform ${
                            expanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {expanded && (
                      <div className="pl-4 border-l border-border/50 ml-2 mb-2">
                        {item.children.map((child) => {
                          const childActive = isChildActive(child.to);
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setIsOpen(false)}
                              className={`block text-[11px] tracking-[0.15em] uppercase font-body py-2 hover:text-gold transition-colors ${
                                childActive
                                  ? "text-gold"
                                  : "text-[hsl(40,30%,70%)]"
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setIsOpen(false)}
                  className={`block ${baseClass}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
